"use client"

import { useEffect, useRef, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { WifiOff } from "lucide-react"
import { captureMessage } from "@/lib/error-reporter"
import {
  probeReachability,
  noteDialogShown,
  noteRetryClicked,
  noteOnlineEvent,
  noteOfflineEvent,
} from "@/lib/net-journal"

// Grace window before a dropped connection is escalated to the blocking
// dialog. On Android `navigator.onLine` flips false→true on every screen
// unlock, Doze exit and wifi↔mobile handover, and a single failed request
// during that window is normal. Showing a modal instantly made the app feel
// broken during blips that resolved themselves in well under a second.
const DISCONNECT_GRACE_MS = 5000

// Backoff for the recovery loop that runs WHILE the dialog is open. Fast at
// first because most reconnects land within a second or two, then settling to
// one probe every 10 s so a long outage isn't hammering a struggling radio.
const RECOVERY_DELAYS_MS = [1000, 2000, 3000, 5000, 8000]
const RECOVERY_STEADY_MS = 10000

// Reachability is delegated to lib/net-journal's two-rung ladder (edge =
// nginx answered, api = Express answered), which also records each probe
// result into the durable journal. `reachable` is the API rung: nginx serving
// a static file while the app tier is wedged is not a state a trader can act
// in, so it must not dismiss this dialog.
async function serverReachable(): Promise<boolean> {
  const r = await probeReachability()
  return r.reachable
}

export function ConnectionMonitor() {
  const [isDisconnected, setIsDisconnected] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  // Mirror of `isDisconnected` readable from the listeners below. The effect
  // registers once with `[]` deps, so it would otherwise close over the
  // initial `false` forever.
  const disconnectedRef = useRef(false)

  useEffect(() => {
    let graceTimer: ReturnType<typeof setTimeout> | null = null
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null
    let recoveryAttempt = 0
    let cancelled = false

    const clearGrace = () => {
      if (graceTimer) {
        clearTimeout(graceTimer)
        graceTimer = null
      }
    }

    const stopRecovery = () => {
      if (recoveryTimer) {
        clearTimeout(recoveryTimer)
        recoveryTimer = null
      }
      recoveryAttempt = 0
    }

    // ── Recovery loop ───────────────────────────────────────────────────────
    // The defect this fixes: once the dialog opened, the ONLY ways out were the
    // `online` event and a manual reload. `online` fires on a false→true
    // transition of `navigator.onLine` — so when the flag never went false in
    // the first place (path blackholed while the radio stayed up, which is the
    // common Android wifi↔cellular case) the event could never fire and the
    // dialog was structurally unable to dismiss itself. sank09 sat on it for
    // 19 minutes. Now the dialog polls for its own recovery and clears the
    // moment the API answers, with no user action at all.
    const markConnected = () => {
      disconnectedRef.current = false
      setIsDisconnected(false)
      setIsRetrying(false)
    }

    const scheduleRecovery = () => {
      // Only ever poll while the dialog is actually up. Without this guard the
      // visibility/online handlers below would fire two probe requests on every
      // tab focus of a perfectly healthy session — pure waste on a mobile radio,
      // and noise in the journal.
      if (!disconnectedRef.current) return
      if (recoveryTimer || cancelled) return
      const delay =
        recoveryAttempt < RECOVERY_DELAYS_MS.length
          ? RECOVERY_DELAYS_MS[recoveryAttempt]
          : RECOVERY_STEADY_MS
      recoveryAttempt++
      recoveryTimer = setTimeout(async () => {
        recoveryTimer = null
        if (cancelled) return
        // Don't burn probes (or wake the radio) while backgrounded; the
        // visibility handler re-arms the loop on return.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") {
          scheduleRecovery()
          return
        }
        const ok = await serverReachable()
        if (cancelled) return
        if (ok) {
          stopRecovery()
          markConnected()
          return
        }
        scheduleRecovery()
      }, delay)
    }

    // Arm the dialog rather than showing it. After DISCONNECT_GRACE_MS we
    // re-verify with a real request before taking over the screen — the
    // `offline` event and a one-off fetch failure are both routinely wrong.
    const scheduleDisconnect = (reason: "event" | "fetch") => {
      if (graceTimer) return // already counting down
      graceTimer = setTimeout(async () => {
        graceTimer = null
        if (cancelled) return
        if (await serverReachable()) return // recovered on its own — stay quiet
        if (cancelled) return
        if (reason === "event") {
          // Capture as `warn` so the dialog event is queryable in Kibana
          // (volume per route, per user, per time) but doesn't trip Rule 11 /
          // Rule 12 Telegram alerts (which only fire on level: error). The
          // reporter's outbox carries this through any sustained outage until
          // connectivity returns. Logged here, AFTER the grace window, so the
          // transient unlock/handover blips no longer generate noise.
          captureMessage("browser_offline: navigator.onLine became false", "warn", {
            component: "connection_monitor_browser_offline",
            routeTag: "browser_offline",
          })
        }
        // Record that the blocking dialog was actually put in front of a
        // trader. Previously only the `offline`-event branch above logged
        // anything, so the far more common case — dialog raised by a failed
        // fetch while the browser still believed it was online — produced NO
        // Kibana record at all. "User saw Connection Lost" was unqueryable.
        noteDialogShown()
        disconnectedRef.current = true
        setIsDisconnected(true)
        scheduleRecovery()
      }, DISCONNECT_GRACE_MS)
    }

    const handleConnectionError = () => {
      // No capture here — api.ts already logged the network_error before
      // dispatching this event. Adding a second captureMessage would
      // dedup against it anyway.
      scheduleDisconnect("fetch")
    }

    const handleOnline = () => {
      noteOnlineEvent()
      // Treat this as a HINT, not proof. `navigator.onLine` flipping true only
      // means the device has a link; the path to our origin may still be dead,
      // and dismissing here on faith is what used to bounce users straight back
      // into the dialog. Let the recovery loop probe immediately and decide.
      clearGrace()
      recoveryAttempt = 0
      if (recoveryTimer) {
        clearTimeout(recoveryTimer)
        recoveryTimer = null
      }
      scheduleRecovery()
    }

    const handleOffline = () => {
      noteOfflineEvent()
      scheduleDisconnect("event")
    }

    // Re-probe the instant the app comes back to the foreground. Android
    // suspends timers in the background, so without this the loop could be
    // mid-sleep for its full steady interval when the user returns.
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return
      recoveryAttempt = 0
      if (recoveryTimer) {
        clearTimeout(recoveryTimer)
        recoveryTimer = null
      }
      scheduleRecovery()
    }

    // Custom backend fetch failure
    window.addEventListener("backend-connection-error", handleConnectionError)

    // Browser native connection status
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      cancelled = true
      clearGrace()
      stopRecovery()
      window.removeEventListener("backend-connection-error", handleConnectionError)
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [])

  const handleRetry = () => {
    setIsRetrying(true)
    noteRetryClicked()
    // Simply reload the page to re-establish connections
    window.location.reload()
  }

  return (
    <Dialog open={isDisconnected} onOpenChange={() => {}}>
      <DialogContent
        className="mx-auto w-[90%] overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-2xl outline-none sm:max-w-md md:w-full"
        // Prevent closing by clicking outside or pressing escape
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center justify-center space-y-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <WifiOff className="h-8 w-8 text-destructive" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold tracking-tight text-foreground">
              Connection Lost
            </DialogTitle>
            <DialogDescription className="pt-2 text-center text-sm font-medium text-muted-foreground">
              We're unable to reach the server. Please check your internet connection or try again.
            </DialogDescription>
          </DialogHeader>
          <div className="w-full pt-4">
            <Button
              onClick={handleRetry}
              disabled={isRetrying}
              className="h-11 w-full text-sm font-bold tracking-wide"
            >
              {isRetrying ? "Reconnecting..." : "Try Again"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
